import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { StatusBadge } from '@/features/invoices/StatusBadge'
import {
  INVOICE_STATUSES,
  STATUS_LABELS,
  daysPastDue,
  exportInvoices,
  formatMoney,
  getAging,
  listInvoices,
  type InvoiceFilters,
  type InvoiceStatus,
  type InvoiceSummary,
} from '@/features/invoices/api'
import { listLocations } from '@/features/orders/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const columns: Column<InvoiceSummary>[] = [
  {
    key: 'invoice_number',
    header: 'Invoice',
    className: 'font-medium',
    sortValue: (invoice) => invoice.invoice_number,
    cell: (invoice) => (
      <Link to={`/invoices/${invoice.id}`} className="hover:underline">
        {invoice.invoice_number}
      </Link>
    ),
  },
  {
    key: 'invoice_date',
    header: 'Raised',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (invoice) => invoice.invoice_date,
    cell: (invoice) => new Date(invoice.invoice_date).toLocaleDateString(),
  },
  {
    key: 'due_date',
    header: 'Due',
    sortValue: (invoice) => invoice.due_date,
    // How late it is matters more than the date itself to whoever is chasing payment, so
    // both are shown -- but only while there is still something to collect.
    cell: (invoice) => {
      const late = daysPastDue(invoice.due_date)
      const chasing = invoice.status !== 'paid' && invoice.status !== 'void'
      return (
        <span className="whitespace-nowrap">
          {new Date(`${invoice.due_date}T00:00:00`).toLocaleDateString()}
          {chasing && late > 0 && <span className="ml-2 text-xs text-danger">{late}d late</span>}
        </span>
      )
    },
  },
  {
    key: 'status',
    header: 'Status',
    sortValue: (invoice) => invoice.status,
    cell: (invoice) => <StatusBadge status={invoice.status} />,
  },
  {
    key: 'total',
    // Sorted on the number, rendered from the string -- the API sends NUMERIC as text so
    // nothing rounds it, and sorting is the one place a Number is harmless.
    header: 'Total',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortValue: (invoice) => Number(invoice.total),
    cell: (invoice) => formatMoney(invoice.total),
  },
  {
    key: 'balance_due',
    header: 'Outstanding',
    className: 'text-right font-medium tabular-nums',
    headerClassName: 'text-right',
    sortValue: (invoice) => Number(invoice.balance_due),
    cell: (invoice) =>
      Number(invoice.balance_due) > 0 ? (
        formatMoney(invoice.balance_due)
      ) : (
        <span className="text-slate-400">—</span>
      ),
  },
]

/**
 * AR aging across everything the signed-in user can see.
 *
 * Totals only. The per-customer rows the API returns are a collections worklist rather than
 * a summary, and they belong on a screen of their own instead of pushing the invoice list
 * below the fold.
 */
function AgingStrip() {
  const { data } = useQuery({
    queryKey: ['invoice-aging'],
    queryFn: () => getAging(),
    retry: false,
  })
  if (!data || Number(data.total_outstanding) === 0) return null

  return (
    <Card>
      <CardHeader
        title="Outstanding"
        description={`Measured against the due date, as of ${new Date(
          `${data.as_of}T00:00:00`,
        ).toLocaleDateString()}.`}
      />
      <dl className="grid grid-cols-2 divide-slate-200 border-t border-slate-200 tablet:grid-cols-5 tablet:divide-x dark:divide-slate-800 dark:border-slate-800">
        {data.totals.map((bucket) => (
          <div key={bucket.label} className="px-5 py-4">
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {bucket.label}
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">
              {formatMoney(bucket.amount)}
            </dd>
            <dd className="text-xs text-slate-500 dark:text-slate-400">
              {bucket.invoice_count} invoice{bucket.invoice_count === 1 ? '' : 's'}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  )
}

export function Invoices() {
  const [filters, setFilters] = useState<InvoiceFilters>({ page: 1 })

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => listInvoices(filters),
    retry: false,
  })

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
    retry: false,
  })
  const locations = locationsPage?.data ?? []

  const invoices = data?.data ?? []
  const meta = data?.meta

  /** Any filter change resets to page 1: page 4 of a narrower result set is usually empty. */
  const set = (patch: Partial<InvoiceFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Invoices</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {meta ? `${meta.total} invoice${meta.total === 1 ? '' : 's'}` : 'Loading…'}
        </p>
      </header>

      <AgingStrip />

      <Card>
        <CardHeader
          title="All invoices"
          description="Raised from shipped orders. A field rep sees the invoices on their own orders."
        />
        <DataTable
          rows={invoices}
          columns={columns}
          getRowId={(invoice) => invoice.id}
          caption="Invoices"
          isLoading={isLoading}
          error={error}
          emptyMessage="No invoices match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="invoice-status">
                Status
              </label>
              <select
                id="invoice-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) =>
                  set({
                    status: event.target.value as InvoiceStatus | '',
                    // Both controls narrow the same thing; letting them both apply would
                    // show an empty table and read as a bug.
                    unpaid_only: false,
                  })
                }
              >
                <option value="">Any status</option>
                {INVOICE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="invoice-location">
                Location
              </label>
              <select
                id="invoice-location"
                className={SELECT_CLASS}
                value={filters.location_id ?? ''}
                onChange={(event) => set({ location_id: event.target.value })}
              >
                <option value="">Any location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="invoice-from">
                From
              </label>
              <Input
                id="invoice-from"
                type="date"
                className="w-auto"
                value={filters.date_from ?? ''}
                onChange={(event) => set({ date_from: event.target.value })}
              />
              <span className="text-sm text-slate-400">to</span>
              <label className="sr-only" htmlFor="invoice-to">
                To
              </label>
              <Input
                id="invoice-to"
                type="date"
                className="w-auto"
                value={filters.date_to ?? ''}
                onChange={(event) => set({ date_to: event.target.value })}
              />

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={Boolean(filters.unpaid_only)}
                  onChange={(event) => set({ unpaid_only: event.target.checked, status: '' })}
                />
                Unpaid only
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={Boolean(filters.unapplied_only)}
                  onChange={(event) => set({ unapplied_only: event.target.checked })}
                />
                Card money to refund
              </label>

              {(filters.status ||
                filters.location_id ||
                filters.date_from ||
                filters.date_to ||
                filters.unpaid_only ||
                filters.unapplied_only) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}

              <ExportButton
                filename="invoices"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportInvoices(filters, format)}
                errorMessage="Could not export the invoices."
              />
            </div>
          }
          search={{
            placeholder: 'Search invoice number…',
            matches: (invoice, query) => invoice.invoice_number.toLowerCase().includes(query),
          }}
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>
    </div>
  )
}
